// UserMenu.jsx — clickable navbar dropdown: Update Profile / Change Password / Reset via email / Logout
import React, { useEffect, useRef, useState } from 'react';
import api, { BACKEND_URL } from '../lib/api';
import { useAuth } from '../lib/auth';
import Portal from './Portal';
import { ChevronDown, User as UserIcon, Key, Mail, LogOut, X, Check } from 'lucide-react';

import { usePresence } from '../lib/presence';

const COLORS = ['#0079BF', '#D29034', '#519839', '#B04632', '#89609E', '#CD5A91', '#4BBF6B', '#00AECC', '#EB5A46', '#5243AA'];

const getAvatarUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${BACKEND_URL}${url}`;
};

function Avatar({ user, size = 30 }) {
  const initials = (user.full_name || user.email || '?').split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div className="tm-avatar" style={{
      width: size, height: size, fontSize: size / 2.5,
      background: user.avatar_url ? `url(${getAvatarUrl(user.avatar_url)}) center/cover` : (user.avatar_color || '#0079BF'),
    }}>{!user.avatar_url && initials}</div>
  );
}

export default function UserMenu({ onLogout, customTrigger }) {
  const { user, refresh } = useAuth();
  const { statuses, changeStatus } = usePresence() || { statuses: {}, changeStatus: () => {} };
  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState(null); // 'profile' | 'password' | 'reset'
  const [anchorRect, setAnchorRect] = useState(null);
  const wrapRef = useRef(null);
  const btnRef = useRef(null);

  const positionPanel = () => {
    if (btnRef.current) setAnchorRect(btnRef.current.getBoundingClientRect());
  };

  useEffect(() => {
    if (!open) return;
    positionPanel();
    const onScrollOrResize = () => positionPanel();
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('scroll', onScrollOrResize, true);
    const h = (e) => {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      const panel = document.querySelector('[data-testid="user-menu-panel"]');
      if (panel && panel.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener('mousedown', h);
    return () => {
      window.removeEventListener('mousedown', h);
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [open]);

  return (
    <div ref={wrapRef} style={{ position: 'relative' }} data-testid="user-menu-wrap">
      {customTrigger ? (
        <div
          ref={btnRef}
          onClick={() => setOpen((v) => !v)}
          data-testid="user-menu-btn"
          style={{ cursor: 'pointer', display: 'block', width: '100%' }}
        >
          {customTrigger}
        </div>
      ) : (
        <button
          ref={btnRef}
          onClick={() => setOpen((v) => !v)}
          data-testid="user-menu-btn"
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: 'rgba(255,255,255,0.12)', border: 'none', color: '#fff',
            padding: '4px 10px 4px 4px', borderRadius: 20, cursor: 'pointer', fontSize: 13,
          }}
          title="Account menu"
        >
          <Avatar user={user} size={26} />
          <span style={{ fontWeight: 500 }}>{user.full_name}</span>
          <ChevronDown size={14} />
        </button>
      )}

      {open && anchorRect && (
        <Portal>
          <div
            className="tm-pop"
            data-testid="user-menu-panel"
            style={{
              position: 'fixed',
              ...(anchorRect.top > window.innerHeight / 2 ? {
                bottom: window.innerHeight - anchorRect.top + 6
              } : {
                top: anchorRect.bottom + 6
              }),
              ...(anchorRect.left < window.innerWidth / 2 ? {
                left: Math.max(8, anchorRect.left)
              } : {
                right: Math.max(8, window.innerWidth - anchorRect.right)
              }),
              width: 240,
              zIndex: 200,
              padding: 8,
            }}
          >
            <div style={{ padding: '6px 8px 10px', borderBottom: '1px solid var(--trello-border)' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--trello-text)' }}>{user.full_name}</div>
              <div style={{ fontSize: 11, color: 'var(--trello-text-muted)' }}>{user.email}</div>
              <div style={{ fontSize: 10, marginTop: 2 }}>
                <span className={`tm-badge tm-badge-${user.role}`}>{user.role}</span>
              </div>
            </div>
            
            {/* Status Selector */}
            <div style={{ padding: '6px 8px', borderBottom: '1px solid var(--trello-border)' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--trello-text-muted)', marginBottom: 4 }}>PRESENCE STATUS</div>
              <select
                value={statuses[user.id]?.presence_status || 'online'}
                onChange={(e) => changeStatus(e.target.value)}
                style={{ width: '100%', fontSize: 12, padding: '4px 6px', borderRadius: 4, border: '1px solid var(--trello-border)' }}
                data-testid="status-select"
              >
                <option value="online">🟢 Online</option>
                <option value="busy">🔴 Busy</option>
                <option value="away">🟡 Away</option>
                <option value="offline">⚪ Offline</option>
              </select>
            </div>

            <button type="button" className="tm-menu-item" onClick={() => { setOpen(false); setModal('profile'); }} data-testid="menu-update-profile">
              <UserIcon size={14} /> <span>Update profile</span>
            </button>

            <button type="button" className="tm-menu-item" onClick={() => { setOpen(false); setModal('password'); }} data-testid="menu-change-password">
              <Key size={14} /> <span>Change password</span>
            </button>
            <button type="button" className="tm-menu-item" onClick={() => { setOpen(false); setModal('reset'); }} data-testid="menu-reset-email">
              <Mail size={14} /> <span>Send reset email</span>
            </button>
            <div style={{ height: 1, background: 'var(--trello-border)', margin: '4px 0' }} />
            <button type="button" className="tm-menu-item" onClick={() => { setOpen(false); onLogout(); }} data-testid="menu-logout">
              <LogOut size={14} /> <span>Log out</span>
            </button>
          </div>
        </Portal>
      )}

      {modal === 'profile' && <ProfileModal user={user} onClose={() => setModal(null)} onSaved={() => { setModal(null); refresh(); }} />}
      {modal === 'password' && <PasswordModal onClose={() => setModal(null)} />}
      {modal === 'reset' && <ResetEmailModal email={user.email} onClose={() => setModal(null)} />}
    </div>
  );
}

function Modal({ title, onClose, children, maxWidth = 460 }) {
  return (
    <Portal>
      <div className="tm-modal-overlay" onClick={onClose}>
        <div className="tm-modal" style={{ maxWidth }} onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between mb-4">
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{title}</h3>
            <button className="tm-btn-ghost" onClick={onClose} data-testid="modal-close"><X size={18} /></button>
          </div>
          {children}
        </div>
      </div>
    </Portal>
  );
}

function ProfileModal({ user, onClose, onSaved }) {
  const { setUser } = useAuth();
  const [name, setName] = useState(user.full_name || '');
  const [color, setColor] = useState(user.avatar_color || COLORS[0]);
  const [avatarUrl, setAvatarUrl] = useState(user.avatar_url || '');
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const fileInputRef = useRef(null);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setErr('');
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await api.post('/uploads/profile', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setAvatarUrl(data.url);
      setUser({ ...user, avatar_url: data.url });
    } catch (ex) {
      setErr(ex.response?.data?.error || 'Profile photo upload failed');
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    setErr('');
    try {
      await api.put('/auth/profile', {
        full_name: name,
        avatar_url: avatarUrl || null,
        avatar_color: color
      });
      onSaved();
    } catch (ex) {
      setErr(ex.response?.data?.error || 'Failed to save');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Update profile" onClose={onClose}>
      <form onSubmit={save} className="space-y-4" data-testid="profile-form">
        <div className="flex flex-col items-center gap-3">
          <div className="relative">
            <div 
              style={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: avatarUrl ? `url(${getAvatarUrl(avatarUrl)}) center/cover` : color,
                border: '2px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 28,
                fontWeight: 'bold',
                color: '#fff',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              {!avatarUrl && (name || user.email || '?').split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase()}
              {uploading && (
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#fff' }}>
                  Uploading...
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <input 
              type="file" 
              ref={fileInputRef} 
              onChange={handlePhotoUpload} 
              style={{ display: 'none' }}
              accept="image/*"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="px-3 py-1 bg-blue-50 border border-blue-200 text-blue-600 rounded-lg text-xs font-semibold hover:bg-blue-100 transition-colors"
            >
              Upload Photo
            </button>
            {avatarUrl && (
              <button
                type="button"
                onClick={() => setAvatarUrl('')}
                className="px-3 py-1 bg-red-50 border border-red-200 text-red-600 rounded-lg text-xs font-semibold hover:bg-red-100 transition-colors"
              >
                Remove Photo
              </button>
            )}
          </div>
        </div>

        <div>
          <label className="tm-label">Full name</label>
          <input className="tm-input" value={name} onChange={(e) => setName(e.target.value)} required data-testid="profile-name" />
        </div>
        <div>
          <label className="tm-label">Email</label>
          <input className="tm-input" value={user.email} disabled />
          <div style={{ fontSize: 11, color: 'var(--trello-text-muted)', marginTop: 4 }}>Email cannot be changed here. Contact your workspace leader.</div>
        </div>
        <div>
          <label className="tm-label">Avatar color</label>
          <div className="flex gap-2 flex-wrap">
            {COLORS.map((c) => (
              <button key={c} type="button" onClick={() => setColor(c)} data-testid={`profile-color-${c}`}
                style={{ width: 30, height: 30, borderRadius: '50%', background: c, border: color === c ? '3px solid var(--trello-text)' : '2px solid #fff', cursor: 'pointer' }} />
            ))}
          </div>
        </div>

        {err && <div className="tm-error-msg" style={{ color: '#DE350B', fontSize: 13 }}>{err}</div>}
        <button type="submit" className="tm-btn w-full" disabled={busy || uploading} data-testid="profile-save">
          {busy ? 'Saving…' : <><Check size={14} style={{ display: 'inline', marginRight: 4, verticalAlign: -2 }} />Save changes</>}
        </button>
      </form>
    </Modal>
  );
}

function PasswordModal({ onClose }) {
  const [cur, setCur] = useState('');
  const [nw, setNw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr(''); setOk(false);
    if (nw.length < 6) return setErr('New password must be at least 6 characters');
    if (nw !== confirm) return setErr('Confirmation does not match new password');
    setBusy(true);
    try {
      await api.post('/auth/change-password', { current_password: cur, new_password: nw });
      setOk(true); setCur(''); setNw(''); setConfirm('');
    } catch (ex) { setErr(ex.response?.data?.error || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="Change password" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="password-form">
        <div>
          <label className="tm-label">Current password</label>
          <input className="tm-input" type="password" value={cur} onChange={(e) => setCur(e.target.value)} required data-testid="pw-current" />
        </div>
        <div>
          <label className="tm-label">New password</label>
          <input className="tm-input" type="password" value={nw} onChange={(e) => setNw(e.target.value)} required data-testid="pw-new" />
        </div>
        <div>
          <label className="tm-label">Confirm new password</label>
          <input className="tm-input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="pw-confirm" />
        </div>
        {err && <div className="tm-error-msg" style={{ color: '#DE350B', fontSize: 13 }} data-testid="pw-error">{err}</div>}
        {ok && <div className="tm-success-msg" style={{ color: '#61BD4F', fontSize: 13 }} data-testid="pw-success">✓ Password updated</div>}
        <button type="submit" className="tm-btn w-full" disabled={busy} data-testid="pw-submit">
          {busy ? 'Updating…' : 'Update password'}
        </button>
      </form>
    </Modal>
  );
}

function ResetEmailModal({ email, onClose }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [link, setLink] = useState(null);
  const send = async () => {
    setBusy(true); setMsg('');
    try {
      const { data } = await api.post('/auth/forgot-password', { email });
      if (data.emailed) setMsg('Reset link sent to your email. It expires in 1 hour.');
      else { setMsg('Reset link generated (email service not configured — use this link):'); setLink(data.reset_link); }
    } catch (ex) { setMsg(ex.response?.data?.error || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="Send reset email" onClose={onClose}>
      <div className="space-y-3" data-testid="reset-form">
        <div style={{ fontSize: 13, color: 'var(--trello-text-muted)' }}>
          We&apos;ll send a password reset link to <b>{email}</b>. The link expires in 1 hour.
        </div>
        <button className="tm-btn w-full" onClick={send} disabled={busy} data-testid="reset-send">
          {busy ? 'Sending…' : 'Send reset link'}
        </button>
        {msg && <div style={{ fontSize: 13 }} data-testid="reset-message">{msg}</div>}
        {link && (
          <div style={{ fontSize: 12, background: 'var(--trello-list-bg)', border: '1px solid #F2D600', padding: 8, borderRadius: 4, wordBreak: 'break-all' }} data-testid="reset-link">
            <a href={link} style={{ color: '#026AA7' }}>{link}</a>
          </div>
        )}
      </div>
    </Modal>
  );
}
